'use client'
import React, { useCallback } from 'react'
import Icon, { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import { PersonalityDatum } from '@crystal-eyes/types'
import IdentityIcon from '@dashboard/components_v2/IdentityIcon'
import Loader from '@crystal-eyes/components/elements/Loader/Loader'
import useEndorsements from '@crystal-eyes/hooks/profiles/useEndorsements'

const MAX_ENDORSEMENTS = 3

interface Identity {
  id: string
  personalInfo?: { firstName?: string }
}

export default function EndorsementArea({
  datum,
  identity,
}: {
  datum: PersonalityDatum
  identity?: Identity
}) {
  const firstName = identity?.personalInfo?.firstName

  const {
    data: { didEndorse, endorsedBy },
    loading,
    doEndorse,
    doUnendorse,
  } = useEndorsements({ datum })

  const processEndorsement = useCallback(() => {
    if (didEndorse) doUnendorse()
    else doEndorse()
  }, [didEndorse, datum, doEndorse, doUnendorse])

  if (!identity) return null
  return (
    <div className="endorsement-area">
      {endorsedBy.length > MAX_ENDORSEMENTS && (
        <div className={`not-shown CE Parented disc-color`}>
          +{endorsedBy.length - MAX_ENDORSEMENTS}
        </div>
      )}

      {endorsedBy.slice(0, MAX_ENDORSEMENTS).map(e => (
        <IdentityIcon key={e.id} identity={e} />
      ))}

      {loading && <Loader blue size={16} />}

      {!loading && didEndorse && (
        <div className="CE tooltip">
          <a
            href="#"
            rel="nofollow"
            aria-label="Remove Endorsement"
            className={`CE Parented disc-color cta`}
            onClick={(evt: React.MouseEvent) => {
              evt.preventDefault()
              processEndorsement()
            }}
          >
            <Icon icon={Icons.Checkmark} />
          </a>
          <div className="tooltip-content">Remove your endorsement?</div>
        </div>
      )}

      {!loading && !didEndorse && (
        <div className="CE tooltip">
          <a
            href="#"
            rel="nofollow"
            aria-label="Endorse"
            className={`CE Parented disc-color cta`}
            onClick={(evt: React.MouseEvent) => {
              evt.preventDefault()
              processEndorsement()
            }}
          >
            <Icon icon={Icons.ThumbsUp} />
          </a>
          <div className="tooltip-content">
            Does this sound {firstName ? `like ${firstName}` : 'correct'}?
            <br />
            If so, give it a thumbs up!
          </div>
        </div>
      )}
    </div>
  )
}
